/**
 * Sirion User Time Tracker — portable vanilla JS service for Heap engagement events.
 * Emits session_start and active_time with encrypted PII. No framework dependencies.
 */
(function (global) {
    'use strict';

    var HEARTBEAT_SECONDS = 120;
    var IDLE_TIMEOUT_MS = 60000;
    var SCROLL_WINDOW_MS = 90000;
    var TICK_INTERVAL_MS = 1000;

    var EVENT_TYPE = {
        SESSION_START: 'session_start',
        ACTIVE_TIME: 'active_time'
    };

    var STORAGE_KEYS = {
        counter: 'sirion_utt_counter',
        lastInteraction: 'sirion_utt_last_interaction',
        lastScroll: 'sirion_utt_last_scroll',
        sessionStart: 'sirion_utt_session_start',
        initialized: 'sirion_utt_initialized'
    };

    var active = false;
    var config = null;
    var storage = null;
    var intervalId = null;
    var counter = 0;
    var lastInteraction = 0;
    var lastScroll = 0;
    var sessionStartTime = null;
    var encryptedPii = null;
    var cryptoKey = null;
    var listeners = [];

    function warn(message) {
        if (typeof console !== 'undefined' && console.warn) {
            console.warn('[SirionUserTimeTracker] ' + message);
        }
    }

    function isStorageAvailable(store) {
        if (!store) {
            return false;
        }
        try {
            var testKey = '__sirion_utt_test__';
            store.setItem(testKey, '1');
            store.removeItem(testKey);
            return true;
        } catch (e) {
            return false;
        }
    }

    function readNumber(key, fallback) {
        try {
            var value = storage.getItem(key);
            if (value === null || value === undefined || value === '') {
                return fallback;
            }
            var parsed = parseInt(value, 10);
            return isNaN(parsed) ? fallback : parsed;
        } catch (e) {
            return fallback;
        }
    }

    function writeNumber(key, value) {
        try {
            storage.setItem(key, String(value));
        } catch (e) {
            warn('Failed to persist ' + key);
        }
    }

    function readString(key) {
        try {
            return storage.getItem(key);
        } catch (e) {
            return null;
        }
    }

    function writeString(key, value) {
        try {
            storage.setItem(key, String(value));
        } catch (e) {
            warn('Failed to persist ' + key);
        }
    }

    function loadState() {
        counter = readNumber(STORAGE_KEYS.counter, 0);
        lastInteraction = readNumber(STORAGE_KEYS.lastInteraction, 0);
        lastScroll = readNumber(STORAGE_KEYS.lastScroll, 0);
        sessionStartTime = readString(STORAGE_KEYS.sessionStart);
    }

    function persistState() {
        writeNumber(STORAGE_KEYS.counter, counter);
        writeNumber(STORAGE_KEYS.lastInteraction, lastInteraction);
        writeNumber(STORAGE_KEYS.lastScroll, lastScroll);
        if (sessionStartTime) {
            writeString(STORAGE_KEYS.sessionStart, sessionStartTime);
        }
    }

    function clearPersistedState() {
        Object.keys(STORAGE_KEYS).forEach(function (k) {
            try {
                storage.removeItem(STORAGE_KEYS[k]);
            } catch (e) {
                /* ignore */
            }
        });
    }

    function normalizeKeyMaterial(keyString) {
        var encoder = new TextEncoder();
        var bytes = encoder.encode(String(keyString || ''));
        var normalized = new Uint8Array(32);
        for (var i = 0; i < 32; i++) {
            normalized[i] = bytes[i % bytes.length] || 0;
        }
        return normalized;
    }

    function bufferToBase64(buffer) {
        var bytes = new Uint8Array(buffer);
        var binary = '';
        for (var i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        if (typeof btoa === 'function') {
            return btoa(binary);
        }
        return null;
    }

    function importEncryptionKey(keyString) {
        if (!global.crypto || !global.crypto.subtle) {
            return Promise.reject(new Error('Web Crypto API unavailable'));
        }
        return global.crypto.subtle.importKey(
            'raw',
            normalizeKeyMaterial(keyString),
            { name: 'AES-GCM' },
            false,
            ['encrypt']
        );
    }

    function encryptField(value, key) {
        var plaintext = value == null ? '' : String(value);
        var iv = global.crypto.getRandomValues(new Uint8Array(12));
        var encoded = new TextEncoder().encode(plaintext);
        return global.crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv }, key, encoded).then(function (cipherBuffer) {
            var ivBase64 = bufferToBase64(iv);
            var cipherBase64 = bufferToBase64(cipherBuffer);
            return ivBase64 + ':' + cipherBase64;
        });
    }

    function encryptPiiFields(pii, key) {
        return Promise.all([
            encryptField(pii.firstName, key),
            encryptField(pii.lastName, key),
            encryptField(pii.email, key),
            encryptField(pii.loginId, key)
        ]).then(function (results) {
            return {
                firstName: results[0],
                lastName: results[1],
                email: results[2],
                loginId: results[3]
            };
        });
    }

    function defaultIsSurfaceVisible() {
        return typeof document !== 'undefined' && document.visibilityState === 'visible';
    }

    function isSurfaceVisible() {
        if (config && typeof config.isSurfaceVisible === 'function') {
            return config.isSurfaceVisible();
        }
        return defaultIsSurfaceVisible();
    }

    function touchInteraction() {
        lastInteraction = Date.now();
    }

    function touchScroll() {
        var now = Date.now();
        lastInteraction = now;
        lastScroll = now;
    }

    function addListener(target, eventName, handler, options) {
        if (!target || !target.addEventListener) {
            return;
        }
        target.addEventListener(eventName, handler, options);
        listeners.push({ target: target, eventName: eventName, handler: handler, options: options });
    }

    function attachEngagementListeners() {
        if (typeof document === 'undefined') {
            return;
        }
        var passive = { passive: true };
        addListener(document, 'mousemove', touchInteraction, passive);
        addListener(document, 'click', touchInteraction, passive);
        addListener(document, 'keydown', touchInteraction, passive);
        addListener(document, 'touchstart', touchInteraction, passive);
        addListener(document, 'scroll', touchScroll, passive);
        addListener(document, 'visibilitychange', function () { /* tick handles visibility */ }, passive);
    }

    function detachEngagementListeners() {
        listeners.forEach(function (entry) {
            try {
                entry.target.removeEventListener(entry.eventName, entry.handler, entry.options);
            } catch (e) {
                /* ignore */
            }
        });
        listeners = [];
    }

    function isEngaged(now) {
        if (!isSurfaceVisible()) {
            return false;
        }
        var interactionDelta = now - lastInteraction;
        var scrollDelta = now - lastScroll;
        return interactionDelta < IDLE_TIMEOUT_MS || scrollDelta < SCROLL_WINDOW_MS;
    }

    function buildPayload(eventType) {
        var context = typeof config.getContext === 'function' ? config.getContext() || {} : {};
        var moduleInfo = typeof config.moduleResolver === 'function'
            ? config.moduleResolver() || {}
            : { module: 'Others', moduleTranslated: 'Others' };
        var timezone = context.timezone || context.userTimezone || '';

        return {
            'Sirion Session ID': context.sessionId != null ? String(context.sessionId) : '',
            'Sirion Session Start Time': sessionStartTime,
            'User ID': context.userId != null ? String(context.userId) : '',
            'Client ID': context.clientId != null ? String(context.clientId) : '',
            'Client Alias': context.clientAlias != null ? String(context.clientAlias) : '',
            'Client User Type': context.clientUserType ? context.clientUserType : '',
            'Environment': context.environment != null ? String(context.environment) : '',
            'User Classification Type': context.userClassification != null ? String(context.userClassification) : '',
            'User Type': context.userType != null ? String(context.userType) : '',
            'Session Type': config.sessionType || '',
            'Module': moduleInfo.module || 'Others',
            'Module Translated': moduleInfo.moduleTranslated || moduleInfo.module || 'Others',
            'Event Timestamp': new Date().toISOString(),
            'Encryption Key ID': config.encryptionKeyId || '',
            'Event Type': eventType,
            'User\'s Timezone': timezone,
            'Tenant\'s Timezone': context.tenantTimezone != null ? String(context.tenantTimezone) : timezone,
            'First Name': encryptedPii.firstName,
            'Last Name': encryptedPii.lastName,
            'Email ID': encryptedPii.email,
            'Login ID': encryptedPii.loginId
        };
    }

    function fireEvent(eventType) {
        if (!config || typeof config.trackFn !== 'function') {
            return;
        }
        try {
            config.trackFn(eventType, buildPayload(eventType));
        } catch (e) {
            warn('Failed to emit ' + eventType + ': ' + (e && e.message ? e.message : e));
        }
    }

    function tick() {
        if (!active) {
            return;
        }
        var now = Date.now();
        if (lastInteraction === 0) {
            lastInteraction = now;
        }
        if (lastScroll === 0) {
            lastScroll = now;
        }

        if (isEngaged(now)) {
            counter += 1;
            if (counter >= HEARTBEAT_SECONDS) {
                fireEvent(EVENT_TYPE.ACTIVE_TIME);
                counter = 0;
            }
        }

        persistState();
    }

    function validateConfig(input) {
        if (!input || typeof input.trackFn !== 'function') {
            throw new Error('SirionUserTimeTracker.init requires trackFn');
        }
        if (typeof input.getContext !== 'function') {
            throw new Error('SirionUserTimeTracker.init requires getContext');
        }
        if (typeof input.getPii !== 'function') {
            throw new Error('SirionUserTimeTracker.init requires getPii');
        }
        if (!input.encryptionKey || !input.encryptionKeyId) {
            throw new Error('SirionUserTimeTracker.init requires encryptionKey and encryptionKeyId');
        }
        return input;
    }

    function init(inputConfig) {
        if (active) {
            return Promise.resolve();
        }

        try {
            config = validateConfig(inputConfig);
        } catch (e) {
            warn(e.message);
            return Promise.reject(e);
        }

        storage = config.storage || (typeof sessionStorage !== 'undefined' ? sessionStorage : null);
        if (!isStorageAvailable(storage)) {
            warn('sessionStorage unavailable — tracker disabled');
            return Promise.resolve();
        }

        var pii = config.getPii() || {};

        return importEncryptionKey(config.encryptionKey)
            .then(function (key) {
                cryptoKey = key;
                return encryptPiiFields(pii, key);
            })
            .then(function (encrypted) {
                encryptedPii = encrypted;
                loadState();

                var now = Date.now();
                if (!lastInteraction) {
                    lastInteraction = now;
                }
                if (!lastScroll) {
                    lastScroll = now;
                }

                var wasInitialized = readString(STORAGE_KEYS.initialized) === '1';

                if (!wasInitialized) {
                    sessionStartTime = new Date().toISOString();
                    writeString(STORAGE_KEYS.sessionStart, sessionStartTime);
                    writeString(STORAGE_KEYS.initialized, '1');
                    fireEvent(EVENT_TYPE.SESSION_START);
                } else if (!sessionStartTime) {
                    sessionStartTime = new Date().toISOString();
                    writeString(STORAGE_KEYS.sessionStart, sessionStartTime);
                }

                attachEngagementListeners();
                intervalId = global.setInterval(tick, TICK_INTERVAL_MS);
                active = true;
                persistState();
            })
            .catch(function (err) {
                warn('Initialization failed: ' + (err && err.message ? err.message : err));
            });
    }

    function destroy() {
        if (intervalId !== null) {
            global.clearInterval(intervalId);
            intervalId = null;
        }
        detachEngagementListeners();

        if (storage && isStorageAvailable(storage)) {
            clearPersistedState();
        }

        active = false;
        config = null;
        storage = null;
        counter = 0;
        lastInteraction = 0;
        lastScroll = 0;
        sessionStartTime = null;
        encryptedPii = null;
        cryptoKey = null;
    }

    function isRunning() {
        return active;
    }

    var SirionUserTimeTracker = {
        init: init,
        destroy: destroy,
        isRunning: isRunning,
        __testHooks: {
            tick: tick,
            isEngaged: isEngaged,
            getCounter: function () { return counter; },
            setCounter: function (value) { counter = value; },
            setLastInteraction: function (value) { lastInteraction = value; },
            setLastScroll: function (value) { lastScroll = value; },
            getSessionStartTime: function () { return sessionStartTime; },
            setSessionStartTime: function (value) { sessionStartTime = value; },
            setActive: function (value) { active = value; },
            setConfig: function (value) { config = value; },
            setStorage: function (value) { storage = value; },
            setEncryptedPii: function (value) { encryptedPii = value; },
            persistState: persistState,
            loadState: loadState,
            clearPersistedState: clearPersistedState,
            fireEvent: fireEvent,
            buildPayload: buildPayload,
            HEARTBEAT_SECONDS: HEARTBEAT_SECONDS,
            IDLE_TIMEOUT_MS: IDLE_TIMEOUT_MS,
            SCROLL_WINDOW_MS: SCROLL_WINDOW_MS,
            STORAGE_KEYS: STORAGE_KEYS,
            EVENT_TYPE: EVENT_TYPE
        }
    };

    global.SirionUserTimeTracker = SirionUserTimeTracker;
})(typeof window !== 'undefined' ? window : global);
